import { IAdvertiserGridRow } from '@/Interfaces/Advertiser.interface';
import { IControlOption } from '@/Interfaces/ControlOption.interface';
import { IRejectValueThunkConfig } from '@/Interfaces/Slices/AsyncThunk.interface';
import { DEFAULT_SORTS } from '@/constants/advertisers';
import {
  formatAdvertisersResponseData,
  formatCategoriesResponseData,
  transformAdvertiserFilters,
} from '@/helpers/advertisers';
import { RootState } from '@/store';
import { archiveAdvertiserEntity, fetchAdvertiserCategories, fetchAdvertisers } from '@/store/api/advertisers.api';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { SearchAdvertisersFilters } from '@/types/advertisers.types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

enum actionPrefix {
  FETCH_ADVERTISERS = 'advertisersSlice/fetch',
  FETCH_CATEGORIES = 'advertisersSlice/fetchCategories',
  ARCHIVE_ADVERTISER = 'advertisersSlice/archive',
}

interface IState {
  data: IAdvertiserGridRow[];
  categories: IControlOption<string, string>[];
  dataRequestId: string | null;
  categoriesRequestId: string | null;
  selectedAdvertiser: IAdvertiserGridRow | null;
  total: number;
  filters: SearchAdvertisersFilters;
}

const initialState: IState = {
  data: [],
  categories: [],
  dataRequestId: null,
  categoriesRequestId: null,
  selectedAdvertiser: null,
  total: 0,
  filters: {
    page: 1,
    limit: 15,
    search: '',
    sort: DEFAULT_SORTS,
    active: null,
    whitelabels: [],
    clients: [],
    isArchived: false,
    unassigned: false,
  },
};

const advertisersSlice = createSlice({
  name: 'advertisers',
  initialState,
  reducers: {
    setSelectedAdvertiser: (state, { payload }: { payload: IAdvertiserGridRow }) => {
      state.selectedAdvertiser = payload;
    },
    clearSelectedAdvertiser: state => {
      state.selectedAdvertiser = null;
    },
    setFilters: (state, { payload }: { payload: Partial<SearchAdvertisersFilters> }) => {
      state.filters = { ...state.filters, page: 1, ...payload };
    },
    resetSortAndPagination: state => {
      state.filters = { ...state.filters, page: 1, sort: DEFAULT_SORTS };
    },
    resetAdvertisersPageState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getAdvertisers.fulfilled, (state, { meta, payload }) => {
        if (state.dataRequestId !== meta.requestId) return;
        state.dataRequestId = null;
        state.data = payload.data;
        state.total = payload.total;
        if (!payload.data.length && state.filters.page > 1) {
          state.filters.page = 1;
        }
      })
      .addCase(getAdvertisers.pending, pendingCaseHandler('data'))
      .addCase(getAdvertisers.rejected, rejectedCaseHandler('data'))
      .addCase(getAdvertiserCategories.fulfilled, fulfilledCaseHandler('categories'))
      .addCase(getAdvertiserCategories.pending, pendingCaseHandler('categories'))
      .addCase(getAdvertiserCategories.rejected, rejectedCaseHandler('categories'))
      .addCase(archiveAdvertiser.fulfilled, (state, { payload }) => {
        const hasAdvertiser = state.data.some(advertiser => advertiser.id === payload);
        if (hasAdvertiser) {
          state.data = state.data.filter(advertiser => advertiser.id !== payload) || [];
          state.total = Math.max(0, (state.total || 0) - 1);
        }
      });
  },
});

export default advertisersSlice.reducer;

export const {
  setSelectedAdvertiser,
  clearSelectedAdvertiser,
  setFilters,
  resetSortAndPagination,
  resetAdvertisersPageState,
} = advertisersSlice.actions;

export const getAdvertisers = createAsyncThunk<
  { data: IAdvertiserGridRow[]; total: number },
  undefined,
  IRejectValueThunkConfig
>(actionPrefix.FETCH_ADVERTISERS, async (_, { rejectWithValue, getState }) => {
  const {
    advertisers: { filters },
  } = getState() as RootState;

  const transformedFilters = transformAdvertiserFilters(filters);

  try {
    const response = await fetchAdvertisers(transformedFilters);
    return formatAdvertisersResponseData(response);
  } catch (err) {
    if (err instanceof Error) {
      return rejectWithValue(err);
    }
    return rejectWithValue(new Error('An unknown error occurred'));
  }
});

export const getAdvertiserCategories = createAsyncThunk<
  IControlOption<string, string>[],
  undefined,
  IRejectValueThunkConfig
>(
  actionPrefix.FETCH_CATEGORIES,
  async (_, { rejectWithValue }) => {
    try {
      const { results } = await fetchAdvertiserCategories();
      return formatCategoriesResponseData(results);
    } catch (err) {
      if (err instanceof Error) {
        return rejectWithValue(err);
      }
      return rejectWithValue(new Error('An unknown error occurred'));
    }
  },
  {
    condition(_, { getState }) {
      const {
        advertisers: { categories },
      } = getState() as RootState;

      return !categories.length;
    },
  },
);

export const archiveAdvertiser = createAsyncThunk<
  number,
  { advertiserId: number; isArchived: boolean },
  IRejectValueThunkConfig
>(actionPrefix.ARCHIVE_ADVERTISER, async ({ advertiserId, isArchived }, { rejectWithValue }) => {
  try {
    const data = await archiveAdvertiserEntity(advertiserId, isArchived);
    return data.id;
  } catch (err) {
    if (err instanceof Error) {
      return rejectWithValue(err);
    }
    return rejectWithValue(new Error('An unknown error occurred'));
  }
});
